import { Box, Typography, darken } from "@mui/material";
import { NoDataContainer } from "../../Common/styles/table";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useAppSelector } from "../../../Redux/hooks";
import { DashboardCardStyle } from "../DashboardContent/style";
import { groupPieColors } from "../DashboardContent/dashboard";

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        p: 1.5,
        backgroundColor: "#FFF",
        borderRadius: "6px",
        border: "1px solid #E5E7EB",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}
    >
      <Typography variant="body2" fontWeight={600} color="text.primary">
        {label}
      </Typography>
      {payload.map((entry: any, index: number) => (
        <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              background: `linear-gradient(180deg, ${entry.color} 0%, ${entry.color}CC 50%, ${entry.color}99 100%)`,
              borderRadius: "2px",
            }}
          />
          <Typography variant="body2" color="text.secondary">
            {entry.name}: {entry.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const CustomBarLabel = (props: any) => {
  const { x, y, width, value } = props;
  if (!value || value === 0) return null;

  return (
    <text
      x={x + width / 2}
      y={y - 5}
      fill="#355962"
      textAnchor="middle"
      fontSize={12}
      fontWeight={600}
    >
      {value}
    </text>
  );
};

const GroupRecruitmentChart = ({ data }: any) => {
  const primaryColor = useAppSelector((state) => state.study.primaryColor);
  const textColor = useAppSelector((state) => state.study.textColor);
  const secondaryTextColor = useAppSelector(
    (state) => state.study.secondaryTextColor
  );

  const transformData = (rawData: any[]) => {
    if (!rawData || rawData.length === 0) return [];

    const metrics = [
      { key: "targetCount", label: "Target" },
      { key: "count", label: "Participants" },
      { key: "consentedParticipants", label: "Consented" },
      { key: "scheduledAppointments", label: "Scheduled" },
      { key: "completedAppointments", label: "Completed" },
    ];

    return metrics.map((metric) => {
      const dataPoint: any = { metric: metric.label };
      rawData.forEach((group) => {
        dataPoint[group.label] = group[metric.key];
      });
      return dataPoint;
    });
  };

  const transformedData = transformData(data?.data || []);
  const groupKeys = data?.data?.map((group: any) => group.label) || [];

  const getGroupColor = (index: number) => {
    return groupPieColors[index % groupPieColors.length] || primaryColor;
  };

  const axisTickStyle = {
    fontSize: 14,
    fontWeight: 500,
    fill: textColor || "#355962",
  };

  const axisLabelStyle = {
    fill: secondaryTextColor || "#9CA3AF",
    fontWeight: 500,
    fontSize: 14,
  };

  return (
    <Box sx={DashboardCardStyle}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight="medium"
          color="text.primary"
        >
          {data?.title || "Group Recruitment Summary (total)"}
        </Typography>
      </Box>
      <Box
        sx={{
          maxHeight: "460px",
        }}
      >
        {!data?.data || data?.data?.length === 0 ? (
          <NoDataContainer>
            <Typography variant="body1" color="gray">
              No data
            </Typography>
          </NoDataContainer>
        ) : (
          <ResponsiveContainer width="100%" height={420}>
            <BarChart
              data={transformedData}
              margin={{ top: 15, right: 10, left: 10, bottom: 20 }}
            >
              <defs>
                {groupKeys.map((groupKey: string, index: number) => {
                  const color = getGroupColor(index);
                  const darkenedColor = darken(color, 0.15);
                  return (
                    <>
                      <linearGradient
                        key={`barGradient-${index}`}
                        id={`barGradient-${index}`}
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor={color} stopOpacity={0.9} />
                        <stop
                          offset="100%"
                          stopColor={color}
                          stopOpacity={0.2}
                        />
                      </linearGradient>
                      <linearGradient
                        key={`barGradientActive-${index}`}
                        id={`barGradientActive-${index}`}
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor={darkenedColor}
                          stopOpacity={0.95}
                        />
                        <stop
                          offset="100%"
                          stopColor={darkenedColor}
                          stopOpacity={0.3}
                        />
                      </linearGradient>
                    </>
                  );
                })}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="metric"
                tick={axisTickStyle}
                axisLine={{ stroke: "#E5E7EB" }}
                tickLine={{ stroke: "#E5E7EB" }}
              />
              <YAxis
                tick={axisTickStyle}
                axisLine={{ stroke: "#E5E7EB" }}
                tickLine={{ stroke: "#E5E7EB" }}
                label={{
                  value: "Count",
                  angle: -90,
                  position: "insideLeft",
                  style: axisLabelStyle,
                }}
              />
              <Tooltip content={<CustomTooltip />} cursor={false} />
              <Legend
                wrapperStyle={{
                  paddingTop: "15px",
                }}
                iconType="circle"
                iconSize={15}
              />
              {groupKeys.map((groupKey: string, index: number) => (
                <Bar
                  key={groupKey}
                  dataKey={groupKey}
                  fill={`url(#barGradient-${index})`}
                  radius={[6, 6, 0, 0]}
                  label={<CustomBarLabel />}
                  activeBar={{
                    fill: `url(#barGradientActive-${index})`,
                  }}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Box>
  );
};

export default GroupRecruitmentChart;
